import { useState } from 'react';
import {
  BanknotesIcon,
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CpuChipIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  model: string;
  tokens: number;
  status: 'completed' | 'pending';
}

interface AccountBalance {
  total: number;
  available: number;
  pending: number;
  currency: string;
  usageBreakdown: {
    gpt4: number;
    gpt35turbo: number;
    claude2: number;
  };
}

interface AccountStatement {
  id: string;
  period: string;
  downloadUrl: string;
  totalTokens: number;
  totalCost: number;
  size: string;
}

interface UsageTier {
  name: string;
  tokensPerMonth: number;
  pricePerThousand: number;
  currentUsage?: number;
}

const mockTransactions: Transaction[] = [
  {
    id: '1',
    date: '2024-01-20',
    description: 'GPT-4 API Usage',
    amount: 125.50,
    type: 'credit',
    model: 'gpt-4',
    tokens: 250000,
    status: 'completed',
  },
  {
    id: '2',
    date: '2024-01-19',
    description: 'GPT-3.5 Turbo API Usage',
    amount: 35.00,
    type: 'credit',
    model: 'gpt-3.5-turbo',
    tokens: 700000,
    status: 'completed',
  },
  {
    id: '3',
    date: '2024-01-18',
    description: 'Claude 2 API Usage',
    amount: 84.00,
    type: 'credit',
    model: 'claude-2',
    tokens: 420000,
    status: 'completed',
  },
];

const mockBalance: AccountBalance = {
  total: 2345.00,
  available: 2100.00,
  pending: 245.00,
  currency: 'USD',
  usageBreakdown: {
    gpt4: 125.50,
    gpt35turbo: 35.00,
    claude2: 84.00,
  },
};

const mockStatements: AccountStatement[] = [
  {
    id: '1',
    period: 'January 2024',
    downloadUrl: '#',
    totalTokens: 1370000,
    totalCost: 244.50,
    size: '156 KB',
  },
  {
    id: '2',
    period: 'December 2023',
    downloadUrl: '#',
    totalTokens: 1250000,
    totalCost: 225.75,
    size: '142 KB',
  },
];

const usageTiers: UsageTier[] = [
  {
    name: 'GPT-4',
    tokensPerMonth: 1000000,
    pricePerThousand: 0.50,
    currentUsage: 250000,
  },
  {
    name: 'GPT-3.5 Turbo',
    tokensPerMonth: 2000000,
    pricePerThousand: 0.05,
    currentUsage: 700000,
  },
  {
    name: 'Claude 2',
    tokensPerMonth: 1500000,
    pricePerThousand: 0.20,
    currentUsage: 420000,
  },
];

export default function Account() {
  const [isStripeLinked, setIsStripeLinked] = useState(false);
  const [isPlaidLinked, setIsPlaidLinked] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="space-y-8">
      {/* Account Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <BanknotesIcon className="h-6 w-6 text-white/90" />
          <h1 className="text-xl font-medium text-white">Account & Billing</h1>
        </div>

        {/* Account Balance */}
        <div className="p-8">
          <div className="grid grid-cols-3 gap-6">
            <div className="glass-panel-dark p-6">
              <div className="text-sm text-white/50">Total Balance</div>
              <div className="mt-2 text-2xl font-semibold text-white">
                {formatCurrency(mockBalance.total)}
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="text-sm text-white/50">Available Balance</div>
              <div className="mt-2 text-2xl font-semibold text-white">
                {formatCurrency(mockBalance.available)}
              </div>
            </div>
            <div className="glass-panel-dark p-6">
              <div className="text-sm text-white/50">Pending Balance</div>
              <div className="mt-2 text-2xl font-semibold text-white">
                {formatCurrency(mockBalance.pending)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Tiers */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <SparklesIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Usage Tiers</h2>
        </div>
        <div className="p-8 grid grid-cols-3 gap-6">
          {usageTiers.map((tier) => (
            <div key={tier.name} className="glass-panel-dark p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <CpuChipIcon className="h-5 w-5 text-white/50" />
                  <h3 className="font-medium text-white">{tier.name}</h3>
                </div>
                <div className="text-sm text-white/50">
                  {formatCurrency(tier.pricePerThousand)}/1K tokens
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Monthly Limit</span>
                  <span className="text-white">{formatNumber(tier.tokensPerMonth)} tokens</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Current Usage</span>
                  <span className="text-white">{formatNumber(tier.currentUsage || 0)} tokens</span>
                </div>
                <div className="h-2 bg-white/[0.05] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-magick-purple rounded-full transition-all duration-500"
                    style={{
                      width: `${((tier.currentUsage || 0) / tier.tokensPerMonth) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Methods */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CreditCardIcon className="h-6 w-6 text-white/90" />
            <h2 className="text-lg font-medium text-white">Payment Methods</h2>
          </div>
          <button className="btn-primary">Add Payment Method</button>
        </div>
        <div className="p-8 grid grid-cols-2 gap-6">
          <div className="glass-panel-dark p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="/stripe-logo.svg" alt="Stripe" className="h-8 w-8" />
                <div>
                  <div className="font-medium text-white">Stripe</div>
                  <div className="text-sm text-white/50">
                    Process payments with Stripe
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsStripeLinked(!isStripeLinked)}
                className={`btn-secondary ${
                  isStripeLinked ? 'bg-white/10' : ''
                }`}
              >
                {isStripeLinked ? 'Connected' : 'Connect'}
              </button>
            </div>
          </div>
          <div className="glass-panel-dark p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img src="/plaid-logo.svg" alt="Plaid" className="h-8 w-8" />
                <div>
                  <div className="font-medium text-white">Plaid</div>
                  <div className="text-sm text-white/50">
                    Connect your bank account
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPlaidLinked(!isPlaidLinked)}
                className={`btn-secondary ${
                  isPlaidLinked ? 'bg-white/10' : ''
                }`}
              >
                {isPlaidLinked ? 'Connected' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="h-6 w-6 text-white/90" />
            <h2 className="text-lg font-medium text-white">Recent Transactions</h2>
          </div>
          <button className="btn-secondary">View All</button>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {mockTransactions.map((transaction) => (
            <div key={transaction.id} className="px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className={`p-2 rounded ${
                      transaction.type === 'credit'
                        ? 'bg-emerald-500/10'
                        : 'bg-rose-500/10'
                    }`}
                  >
                    {transaction.type === 'credit' ? (
                      <ArrowUpIcon
                        className="h-5 w-5 text-emerald-300"
                        aria-hidden="true"
                      />
                    ) : (
                      <ArrowDownIcon
                        className="h-5 w-5 text-rose-300"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {transaction.description}
                    </div>
                    <div className="text-sm text-white/50">
                      {transaction.model} • {formatNumber(transaction.tokens)} tokens • {transaction.date}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className={`text-sm font-medium ${
                      transaction.type === 'credit'
                        ? 'text-emerald-300'
                        : 'text-rose-300'
                    }`}
                  >
                    {transaction.type === 'credit' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </div>
                  <div className="text-sm text-white/50">
                    {transaction.status}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statements */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center space-x-3">
          <DocumentTextIcon className="h-6 w-6 text-white/90" />
          <h2 className="text-lg font-medium text-white">Account Statements</h2>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {mockStatements.map((statement) => (
            <div
              key={statement.id}
              className="px-8 py-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-white/[0.05] p-2 rounded">
                  <DocumentTextIcon className="h-5 w-5 text-white/90" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">
                    {statement.period}
                  </div>
                  <div className="text-sm text-white/50">
                    {formatNumber(statement.totalTokens)} tokens • {formatCurrency(statement.totalCost)} • {statement.size}
                  </div>
                </div>
              </div>
              <button className="btn-secondary text-sm">Download</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
